<template>
  <div class="news-page">
    <site-header />

    <section class="news-page__hero">
      <div class="core-container">
        <h1 class="news-page__hero-title">商标资讯</h1>
      </div>
    </section>

    <main class="news-page__main">
      <div class="core-container">
        <section class="news-page__card">
          <p v-if="loading" class="news-page__status">加载中...</p>
          <p v-else-if="error" class="news-page__status">{{ error }}</p>
          <p v-else-if="!articles.length" class="news-page__status">暂无资讯，请稍后再看。</p>
          <template v-else>
            <article
              v-for="item in articles"
              :key="item.id"
              class="news-item"
            >
              <router-link :to="'/news/' + item.id" class="news-item__link">
                <h2 class="news-item__title">{{ item.title }}</h2>
                <p class="news-item__summary">{{ item.summary || '暂无摘要' }}</p>
                <p class="news-item__meta">发布时间：{{ formatDate(item.published_at || item.created_at) }}</p>
              </router-link>
            </article>

            <div v-if="totalPages > 1" class="news-pagination">
              <button
                type="button"
                class="news-pagination__btn"
                :disabled="page <= 1"
                @click="changePage(page - 1)"
              >上一页</button>
              <span class="news-pagination__info">{{ page }} / {{ totalPages }}</span>
              <button
                type="button"
                class="news-pagination__btn"
                :disabled="page >= totalPages"
                @click="changePage(page + 1)"
              >下一页</button>
            </div>
          </template>
        </section>
      </div>
    </main>

    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import { fetchSiteArticles, SITE_ARTICLE_PAGE_SIZE } from '@/api/site'

export default {
  name: 'NewsListPage',
  components: {
    SiteHeader,
    SiteFooter
  },
  data() {
    return {
      loading: true,
      error: null,
      articles: [],
      page: 1,
      totalPages: 1
    }
  },
  watch: {
    '$route.query.page': {
      immediate: true,
      handler(value) {
        var nextPage = parseInt(value, 10)
        this.page = Number.isNaN(nextPage) || nextPage < 1 ? 1 : nextPage
        this.loadArticles()
      }
    }
  },
  methods: {
    formatDate(value) {
      if (!value) return '-'
      var date = new Date(String(value).replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) return value
      var y = date.getFullYear()
      var m = String(date.getMonth() + 1).padStart(2, '0')
      var d = String(date.getDate()).padStart(2, '0')
      return y + '年' + m + '月' + d + '日'
    },

    loadArticles() {
      this.loading = true
      this.error = null

      fetchSiteArticles(this.page, SITE_ARTICLE_PAGE_SIZE)
        .then(data => {
          if (!data.enabled) {
            this.articles = []
            this.error = '资讯暂未开通'
            return
          }
          this.articles = data.list || []
          this.totalPages = data.total_pages || 1
        })
        .catch(() => {
          this.error = '加载失败，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    },

    changePage(nextPage) {
      if (nextPage < 1 || nextPage > this.totalPages) return
      this.$router.push({
        path: '/news',
        query: nextPage > 1 ? { page: String(nextPage) } : {}
      })
    }
  }
}
</script>

<style scoped>
.news-page {
  min-height: 100vh;
  background: var(--color-bg-main);
}

.news-page__hero {
  background: linear-gradient(
    90deg,
    var(--color-banner-fill-left) 0%,
    var(--color-banner-fill-left) 42%,
    var(--color-banner-fill-right) 58%,
    var(--color-banner-fill-right) 100%
  );
  padding: var(--space-2xl) 0;
}

.news-page__hero-title {
  margin: 0;
  text-align: center;
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--color-text-inverse);
}

.news-page__main {
  padding: var(--space-lg) 0 var(--space-xl);
}

.news-page__card {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--space-xl);
  min-height: 320px;
}

.news-item + .news-item {
  border-top: 1px solid var(--color-border-light);
  margin-top: var(--space-lg);
  padding-top: var(--space-lg);
}

.news-item__link {
  display: block;
  color: inherit;
}

.news-item__title {
  margin: 0 0 var(--space-sm);
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  transition: color 0.2s;
}

.news-item__link:hover .news-item__title {
  color: var(--color-primary);
}

.news-item__summary {
  margin: 0 0 var(--space-md);
  font-size: var(--font-size-md);
  line-height: 1.8;
  color: var(--color-text-secondary);
}

.news-item__meta {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.news-page__status {
  text-align: center;
  padding: var(--space-2xl) 0;
  color: var(--color-text-muted);
}

.news-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-lg);
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}

.news-pagination__btn {
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-page);
  color: var(--color-text-primary);
  cursor: pointer;
}

.news-pagination__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.news-pagination__info {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}
</style>
